import { useEffect, useState } from "react";
import axios from "axios";
import { buildServiceUrl } from "../../utils/urlBuilder";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import BreadCrumb from "../../components/HeaderComponents/BreadCrum";
import FilterSearch from "../../components/Filtering/FilterSearch";
import SearchButton from "../../components/buttons/SearchButton";
import FilterRightBox from "../../components/Filtering/FilterRightBoxOffering";
import DataTable from "../../components/DataTable/DataTable";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import ConfirmDialog from "../../components/DataTable/ConfirmDialog";
import { useTableControls } from "../../hooks/useTableControls";
import { toast } from "react-toastify";
import EditUserRolesForm from "./AdminUtils/EditUserRolesForm";
import CreateUserDialog from "./AdminUtils/CreateUserDialog";
import { Popover } from "../../components/DataTable/Popover";
import CustomButton from "../../components/buttons/CustomButton";
import StatusChip from "../../components/StatusChip/StatusChip";

const UserControl = () => {
  const {
    tableData,
    setTableData,
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    setPagination,
    rowCount,
    setRowCount,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);
  const { t } = useTranslation();
  const { dialogOpen, dialogConfig, openDialog, closeDialog } =
    useConfirmDialog();
  const { isAdmin, token, decoded } = useAuthRoles();
  const [openPetition, setOpenPetition] = useState(false);
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleOpenPetition = (row) => {
    setSelectedUser(row);
    setOpenPetition(true);
  };

  const handleToggleUser = (user) => {
    const enable = !user.enabled;

    openDialog({
      title: enable
        ? t("confirmDialog.enableTitle")
        : t("confirmDialog.disableTitle"),
      message: enable
        ? t("confirmDialog.enableMessage", { username: user.username })
        : t("confirmDialog.disableMessage", { username: user.username }),
      onConfirm: async () => {
        await toggleUserEnabled(user.id, enable);
        const updated = await listPendingUsers();
        setTableData(updated);
      },
    });
  };

  const handleDeleteUser = (user) => {
    openDialog({
      title: t("confirmDialog.deleteTitle"),
      message: t("confirmDialog.deleteMessage", { username: user.username }),
      onConfirm: async () => {
        await deleteUser(user.id);
        const updated = await listPendingUsers();
        setTableData(updated);
      },
    });
  };

  const handleCloseSession = (user) => {
    openDialog({
      title: t("confirmDialog.sessionTitle"),
      message: t("confirmDialog.sessionMessage", { username: user.username }),
      onConfirm: async () => {
        await logoutUser(user.id);
        const updated = await listPendingUsers();
        setTableData(updated);
      },
    });
  };

  async function toggleUserEnabled(userId, enable) {
    const userToken = token;
    const res = await axios.get(
      `${buildServiceUrl("keycloak", `admin/realms/DSB/users/${userId}`)}`,
      { headers: { Authorization: `Bearer ${userToken}` } },
    );
    const user = res.data;
    try {
      await axios.put(
        `${buildServiceUrl("keycloak", `admin/realms/DSB/users/${userId}`)}`,
        { ...user, enabled: enable },
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
      toast.success(t("toaster.userStatus"));
    } catch (error) {
      toast.error(t("toaster.userError"));
    }
  }

  async function logoutUser(userId) {
    const userToken = token;
    try {
      await axios.post(
        `${buildServiceUrl(
          "keycloak",
          `admin/realms/DSB/users/${userId}/logout`,
        )}`,
        {},
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
      toast.success(t("toaster.userSession"));
    } catch (error) {
      toast.error(t("toaster.userError"));
    }
  }

  async function getTotalUserCount(userToken) {
    try {
      const res = await axios.get(
        `${buildServiceUrl("keycloak", "admin/realms/DSB/users/count")}`,
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching user count:", error);
      return 0;
    }
  }

  async function deleteUser(userId) {
    const userToken = token;
    try {
      await axios.delete(
        `${buildServiceUrl("keycloak", `admin/realms/DSB/users/${userId}`)}`,
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
      toast.success(t("toaster.userDeleted"));
    } catch (error) {
      toast.error(t("toaster.userError"));
    }
  }

  const refreshUsers = async () => {
    if (!token || !isAdmin) return;

    const totalCount = await getTotalUserCount(token);
    setRowCount(totalCount);

    const users = await listPendingUsers();
    setTableData(users);
  };

  const columns = [
    {
      header: t("usermanagement.name"),
      accessorKey: "firstName",
      meta: { width: "15%" },
      cell: ({ getValue }) => (
        <div className="centered-cell">
          <span className="bold">{getValue()}</span>
        </div>
      ),
    },
    {
      header: t("register.surname"),
      accessorKey: "lastName",
      meta: { width: "20%" },
      cell: ({ getValue }) => (
        <div className="centered-cell">
          <span className="bold">{getValue()}</span>
        </div>
      ),
    },
    {
      header: t("usermanagement.email"),
      accessorKey: "email",
      meta: { width: "20%" },
    },
    {
      header: t("usermanagement.role"),
      accessorKey: "role",
      meta: { width: "15%" },
      cell: ({ getValue }) => {
        const roles = getValue();
        if (!roles || (Array.isArray(roles) && roles.length === 0)) {
          return null;
        }
        return (
          <div className="centered-cell highlight-container">
            {Array.isArray(roles) ? (
              roles.map((r, idx) => (
                <span key={idx} className={`roles_span role-${r}`}>
                  {t(`roles.${r}`, r)}
                </span>
              ))
            ) : (
              <span className={`roles_span role-${roles}`}>
                {t(`roles.${roles}`, roles)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      header: t("profilePage.statusTitle"),
      accessorKey: "enabled",
      meta: { width: "10%" },
      cell: ({ getValue }) => (
        <div className="centered-cell">
          <StatusChip
            label={t(`profilePage.status.${getValue()}`)}
            value={String(getValue())}
          />
        </div>
      ),
    },
    {
      header: t("profilePage.personalData.organization"),
      accessorFn: (row) => row.attributes?.organization ?? "",
      meta: { width: "20%" },
    },
  ];
  const filteredData = useFilteredData(tableData, searchTerm, filters, columns);

  async function listPendingUsers() {
    const userToken = token;
    const first = (pagination.pageIndex - 1) * pagination.pageSize;
    const max = pagination.pageSize;

    const res = await axios.get(
      `${buildServiceUrl(
        "keycloak",
        `admin/realms/DSB/users?first=${first}&max=${max}`,
      )}`,
      { headers: { Authorization: `Bearer ${userToken}` } },
    );

    const users = res.data;
    const otherUsers = users.filter((u) => u.id !== decoded.sub);

    const usersWithExtraData = await Promise.all(
      otherUsers.map(async (userItem) => {
        try {
          // Roles
          const rolesRes = await axios.get(
            `${buildServiceUrl(
              "keycloak",
              `admin/realms/DSB/users/${userItem.id}/role-mappings/realm`,
            )}`,
            { headers: { Authorization: `Bearer ${userToken}` } },
          );
          const roles = rolesRes.data
            .map((r) => r.name)
            .filter((r) => r !== "default-roles-dsb");

          // Sesiones activas
          const sessionsRes = await axios.get(
            `${buildServiceUrl(
              "keycloak",
              `admin/realms/DSB/users/${userItem.id}/sessions`,
            )}`,
            { headers: { Authorization: `Bearer ${userToken}` } },
          );
          const hasActiveSession = sessionsRes.data.length > 0;

          return {
            ...userItem,
            role: roles,
            hasActiveSession,
          };
        } catch (err) {
          console.error(
            `Error fetching data for user ${userItem.username}:`,
            err,
          );
          return { ...userItem, role: "N/A", hasActiveSession: false };
        }
      }),
    );

    return usersWithExtraData;
  }

  useEffect(() => {
    if (!token || !isAdmin) return;

    refreshUsers().catch((err) => console.error("Error fetching users:", err));
  }, [token, isAdmin, pagination.pageIndex, pagination.pageSize]);

  return (
    <section className="newstyle">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>
      <div className="container containerflex">
        <div key={t.language} className="w90 labeltittle">
          <h2 className="tittlecatalog">{t("usermanagement.title")}</h2>
          <CustomButton
            onClick={() => setIsCreateUserDialogOpen(true)}
            startIcon={<AddIcon />}
          >
            {t("usermanagement.createUser")}
          </CustomButton>
        </div>
        <div key={t.language} className="w86 filteractions pt3">
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>
        <FilterRightBox
          open={open}
          onClose={handleClose}
          onApplyFilters={handleApplyFilters}
        />
        <div className="w90 bgwhite mtb2">
          <div className="plr3 ptb">
            <DataTable
              columns={columns}
              data={filteredData}
              showActions={isAdmin}
              onPermission={handleOpenPetition}
              onInhability={handleToggleUser}
              onDeleteUser={handleDeleteUser}
              onCloseSession={handleCloseSession}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
            />
            <ConfirmDialog
              open={dialogOpen}
              title={dialogConfig.title}
              message={dialogConfig.message}
              confirmText={dialogConfig.confirmText}
              cancelText={dialogConfig.cancelText}
              onClose={closeDialog}
              onConfirm={async () => {
                if (dialogConfig.onConfirm) await dialogConfig.onConfirm();
                closeDialog();
              }}
            />
          </div>
        </div>
      </div>
      {openPetition && selectedUser ? (
        <Popover open={openPetition} onClose={() => setOpenPetition(false)}>
          <EditUserRolesForm
            userInfo={selectedUser}
            closePopover={() => setOpenPetition(false)}
            onUpdateTable={listPendingUsers}
            setTableData={setTableData}
          />
        </Popover>
      ) : null}
      {isCreateUserDialogOpen ? (
        <CreateUserDialog
          open={isCreateUserDialogOpen}
          onClose={() => setIsCreateUserDialogOpen(false)}
          onSuccess={async () => {
            setIsCreateUserDialogOpen(false);
            await refreshUsers();
          }}
        />
      ) : null}
    </section>
  );
};

export default UserControl;
